import { Box, Button, Group, Image, List, rem, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconUpload, IconPhoto, IconX, IconPhotoPlus } from '@tabler/icons-react';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { useState } from 'react';
import { useTimeout } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { useTranslation } from "react-i18next";
import { MAX_IMAGE_UPLOAD_SIZE, readableUploadErrors } from "./uploadLimits";

export const MultipleUpload = (props) => {

    const { t } = useTranslation();

    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState([]);
    const errorTimeout = useTimeout(() => setErrors([]), 3000);

    const previews = files.map((file, index) => {
        const imageUrl = URL.createObjectURL(file);
        return <Image key={index} src={imageUrl} onLoad={() => URL.revokeObjectURL(imageUrl)} />;
    });

    const onDrop = (files) => {
        const readers = files.map(file => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
            });
        });

        Promise.all(readers)
            .then((base64Files) => {
                props?.onChange(base64Files);
                setFiles(files);
            })
            .catch((err) => {
                console.error("File reading error:", err);
            });
    }

    const deleteAllModal = () => modals.openConfirmModal({
        title: t('upload.deleteAllConfirm'),
        labels: { confirm: t('common.yes'), cancel: t('common.cancel') },
        confirmProps: { color: 'red' },
        onConfirm: () => setFiles([]),
    });

    return (
        <Box>
            <Dropzone
                onDrop={onDrop}
                onReject={(errors) => {
                    setErrors(readableUploadErrors(errors[0].errors, props.maxSize ?? MAX_IMAGE_UPLOAD_SIZE, t));
                    errorTimeout.start();
                }}
                maxSize={MAX_IMAGE_UPLOAD_SIZE}
                accept={[
                    MIME_TYPES.png,
                    MIME_TYPES.jpeg,
                    MIME_TYPES.gif,
                    MIME_TYPES.webp,
                ]}
                {...props}

                {...(errors?.length ? { style: { borderColor: 'var(--mantine-color-red-6)' } } : {})}
            >
                <Group justify="center" gap="xl" mih={56} style={{ pointerEvents: 'none' }}>
                    <Dropzone.Accept>
                        <IconUpload
                            style={{ width: rem(24), height: rem(24), color: 'var(--mantine-color-blue-6)' }}
                            stroke={1.5}
                        />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                        <IconX
                            style={{ width: rem(24), height: rem(24), color: 'var(--mantine-color-red-6)' }}
                            stroke={1.5}
                        />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                        <Stack align="center" gap={2}>
                            <IconPhotoPlus
                                style={{ width: rem(24), height: rem(24), color: errors?.length ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-dimmed)' }}
                                stroke={1.5}
                            />
                            <Text size="sm" c={errors?.length ? "red" : "dimmed"} inline>
                                {t('upload.clickOrDrag')}
                            </Text>
                            <Text size="sm" c={errors?.length ? "red" : "dimmed"} inline>
                                {t('upload.toUploadImages')}
                            </Text>
                        </Stack>

                    </Dropzone.Idle>
                </Group>
            </Dropzone>
            <List size="xs">
                {
                    errors?.map(({ message }, index) => <List.Item key={`error-${index}`} c="red">{message}</List.Item>)
                }
            </List>
            <SimpleGrid cols={{ base: 1, sm: 4 }} mt={previews.length > 0 ? 'xl' : 0}>
                {previews}
            </SimpleGrid>
            {
                previews.length > 0 && (
                    <Button mt="md" variant="light" color="red" size="sm" radius="md" onClick={deleteAllModal}>{t('upload.deleteAll')}</Button>
                )
            }
        </Box>
    );
}
